import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  MapPin,
  FileLock2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { GroupedProvider } from "../utils/group-time-entries";
import { calculateHours, formatHours } from "../utils/group-time-entries";
import {
  formatDateInTimezone,
  formatTimeInTimezone,
} from "../../utils/time-entry-schema";
import { TimeEntryOrgNote } from "./TimeEntryOrgNote";
import { AdHocEntryBadge } from "./AdHocEntryBadge";
import { EntryDifferenceBadge } from "./EntryDifferenceBadge";
import {
  NOT_SCHEDULED_LABEL,
  getEntryHoursDifference,
  isAdHocEntry,
} from "../utils/ad-hoc-entry";
import { WageSummary } from "./time-entry-review-table/WageSummary";
import { EditCustomWageButton } from "./time-entry-review-table/EditCustomWageButton";
import { SwitchOrganizationWageSetButton } from "./time-entry-review-table/SwitchOrganizationWageSetButton";

interface TimeEntryReviewTableProps {
  groupedEntries: GroupedProvider[];
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onApproveSelected: () => void;
  onApproveOne: (entryId: string) => void;
  onReject: (
    entryId: string,
    providerName: string,
    facilityName: string,
    date: string,
  ) => void;
  isApproving: boolean;
  canApprove?: boolean;
  organizationBusinessUnitId: string;
  onOrgNoteSaved: () => void;
}

export function TimeEntryReviewTable({
  groupedEntries,
  selectedIds,
  onSelectionChange,
  onApproveSelected,
  onApproveOne,
  onReject,
  isApproving,
  canApprove = true,
  organizationBusinessUnitId,
  onOrgNoteSaved,
}: TimeEntryReviewTableProps) {
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(
    new Set(groupedEntries.map((g) => g.providerId)),
  );

  const toggleProvider = (providerId: string) => {
    const newExpanded = new Set(expandedProviders);
    if (newExpanded.has(providerId)) {
      newExpanded.delete(providerId);
    } else {
      newExpanded.add(providerId);
    }
    setExpandedProviders(newExpanded);
  };

  const toggleEntry = (entryId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    onSelectionChange(newSelected);
  };

  const toggleAllInFacility = (entries: { timeEntryId: string }[]) => {
    const entryIds = entries.map((e) => e.timeEntryId);
    const allSelected = entryIds.every((id) => selectedIds.has(id));

    const newSelected = new Set(selectedIds);
    if (allSelected) {
      entryIds.forEach((id) => newSelected.delete(id));
    } else {
      entryIds.forEach((id) => newSelected.add(id));
    }
    onSelectionChange(newSelected);
  };

  const toggleAllInProvider = (provider: GroupedProvider) => {
    const entryIds = provider.facilities.flatMap((f) =>
      f.entries.map((e) => e.timeEntryId),
    );
    const allSelected = entryIds.every((id) => selectedIds.has(id));

    const newSelected = new Set(selectedIds);
    if (allSelected) {
      entryIds.forEach((id) => newSelected.delete(id));
    } else {
      entryIds.forEach((id) => newSelected.add(id));
    }
    onSelectionChange(newSelected);
  };

  const formatWageType = (wageType: string) => {
    return wageType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="space-y-4">
      {/* Bulk action bar */}
      {canApprove && selectedIds.size > 0 && (
        <div className="sticky top-0 z-10 bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm">
          <div className="flex items-center gap-3">
            <Badge className="bg-blue-600 text-white whitespace-nowrap">
              {selectedIds.size} selected
            </Badge>
            <span className="text-sm text-slate-600">
              Ready to approve selected time entries?
            </span>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSelectionChange(new Set())}
              disabled={isApproving}
              className="flex-1 sm:flex-none"
            >
              Clear Selection
            </Button>
            <Button
              size="sm"
              onClick={onApproveSelected}
              disabled={isApproving}
              className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
            >
              {isApproving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve {selectedIds.size}{" "}
                  {selectedIds.size === 1 ? "Entry" : "Entries"}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Provider groups */}
      {groupedEntries.map((provider) => {
        const isExpanded = expandedProviders.has(provider.providerId);
        const providerEntryIds = provider.facilities.flatMap((f) =>
          f.entries.map((e) => e.timeEntryId),
        );
        const allProviderSelected = providerEntryIds.every((id) =>
          selectedIds.has(id),
        );
        const someProviderSelected =
          providerEntryIds.some((id) => selectedIds.has(id)) &&
          !allProviderSelected;

        return (
          <Card
            key={provider.providerId}
            className="overflow-hidden border-slate-200"
          >
            <Collapsible open={isExpanded}>
              <CollapsibleTrigger asChild>
                <div
                  className="flex items-center justify-between px-4 py-3 bg-slate-100 hover:bg-slate-200 transition-colors cursor-pointer"
                  onClick={() => toggleProvider(provider.providerId)}
                >
                  <div className="flex items-center gap-3">
                    {canApprove && (
                      <Checkbox
                        checked={allProviderSelected}
                        ref={(el) => {
                          if (el) {
                            (el as HTMLButtonElement).dataset.state =
                              someProviderSelected
                                ? "indeterminate"
                                : undefined;
                          }
                        }}
                        onCheckedChange={() => toggleAllInProvider(provider)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-slate-600" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-600" />
                    )}
                    <span className="font-semibold text-slate-900">
                      {provider.providerName}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {provider.totalEntries}{" "}
                      {provider.totalEntries === 1 ? "entry" : "entries"}
                    </Badge>
                  </div>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent>
                {provider.facilities.map((facility) => {
                  const facilityEntryIds = facility.entries.map(
                    (e) => e.timeEntryId,
                  );
                  const allFacilitySelected = facilityEntryIds.every((id) =>
                    selectedIds.has(id),
                  );

                  return (
                    <div key={facility.facilityId} className="border-t">
                      {/* Facility header */}
                      <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {canApprove && (
                            <Checkbox
                              checked={allFacilitySelected}
                              onCheckedChange={() =>
                                toggleAllInFacility(facility.entries)
                              }
                            />
                          )}
                          <MapPin className="h-4 w-4 text-slate-400" />
                          <div>
                            <div className="font-medium text-slate-800">
                              {facility.facilityName}
                            </div>
                            {facility.facilityLocation && (
                              <div className="text-xs text-slate-500">
                                {facility.facilityLocation}
                              </div>
                            )}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {facility.entries.length}{" "}
                          {facility.entries.length === 1 ? "shift" : "shifts"}
                        </Badge>
                      </div>

                      {/* Desktop table */}
                      <div className="hidden sm:block">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-white text-xs">
                              {canApprove && (
                                <TableHead className="w-12"></TableHead>
                              )}
                              <TableHead>Date</TableHead>
                              <TableHead>Scheduled</TableHead>
                              <TableHead>Submitted</TableHead>
                              <TableHead>Difference</TableHead>
                              <TableHead>Wage Type</TableHead>
                              <TableHead>Wage</TableHead>
                              <TableHead>Org Note</TableHead>
                              {canApprove && (
                                <TableHead className="text-right">
                                  Actions
                                </TableHead>
                              )}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {facility.entries.map((entry) => {
                              const isSelected = selectedIds.has(
                                entry.timeEntryId,
                              );
                              const scheduledHours = calculateHours(
                                new Date(entry.scheduledStartsAt),
                                new Date(entry.scheduledEndsAt),
                              );
                              const submittedHours = calculateHours(
                                new Date(entry.submittedStartsAt),
                                new Date(entry.submittedEndsAt),
                              );
                              const difference = getEntryHoursDifference(entry);

                              return (
                                <TableRow
                                  key={entry.timeEntryId}
                                  className={cn(
                                    "hover:bg-slate-50",
                                    canApprove && isSelected && "bg-blue-50",
                                  )}
                                >
                                  {canApprove && (
                                    <TableCell>
                                      <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() =>
                                          toggleEntry(entry.timeEntryId)
                                        }
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell>
                                    <div className="font-medium">
                                      {formatDateInTimezone(
                                        entry.submittedStartsAt,
                                        entry.timezoneId,
                                      )}
                                    </div>
                                    {isAdHocEntry(entry) && (
                                      <AdHocEntryBadge className="mt-1" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isAdHocEntry(entry) && (
                                      <div className="text-sm text-slate-500">
                                        {NOT_SCHEDULED_LABEL}
                                      </div>
                                    )}
                                    {!isAdHocEntry(entry) && (
                                      <div className="text-sm">
                                        <div className="text-slate-600">
                                          {formatTimeInTimezone(
                                            entry.scheduledStartsAt,
                                            entry.timezoneId,
                                          )}{" "}
                                          -{" "}
                                          {formatTimeInTimezone(
                                            entry.scheduledEndsAt,
                                            entry.timezoneId,
                                          )}
                                        </div>
                                        <div className="text-slate-400">
                                          {formatHours(scheduledHours)}
                                        </div>
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">
                                      <div className="font-medium text-slate-900">
                                        {formatTimeInTimezone(
                                          entry.submittedStartsAt,
                                          entry.timezoneId,
                                        )}{" "}
                                        -{" "}
                                        {formatTimeInTimezone(
                                          entry.submittedEndsAt,
                                          entry.timezoneId,
                                        )}
                                      </div>
                                      <div className="text-blue-600">
                                        {formatHours(submittedHours)}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <EntryDifferenceBadge
                                      difference={difference}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">
                                      <div>
                                        {formatWageType(entry.baseWageType)}
                                      </div>
                                      {entry.isOvertime && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs mt-1 bg-blue-50 text-blue-700 border-blue-200"
                                        >
                                          Overtime
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="min-w-[140px] max-w-[220px]">
                                    <div className="space-y-1">
                                      <WageSummary entry={entry} />
                                      {canApprove && (
                                        <>
                                          <EditCustomWageButton
                                            entry={entry}
                                            organizationBusinessUnitId={
                                              organizationBusinessUnitId
                                            }
                                            onSaved={onOrgNoteSaved}
                                          />
                                          {entry.organizationWageSet && (
                                            <SwitchOrganizationWageSetButton
                                              entry={entry}
                                              organizationWageSet={
                                                entry.organizationWageSet
                                              }
                                              organizationBusinessUnitId={
                                                organizationBusinessUnitId
                                              }
                                              onSaved={onOrgNoteSaved}
                                            />
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <TimeEntryOrgNote
                                      timeEntryId={entry.timeEntryId}
                                      organizationBusinessUnitId={
                                        organizationBusinessUnitId
                                      }
                                      orgNote={entry.orgNote}
                                      canEdit={canApprove}
                                      popover
                                      onSaved={onOrgNoteSaved}
                                    />
                                  </TableCell>
                                  {canApprove && (
                                    <TableCell className="text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            onApproveOne(entry.timeEntryId)
                                          }
                                          disabled={isApproving}
                                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                        >
                                          <CheckCircle className="h-4 w-4 mr-1" />
                                          Approve
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            onReject(
                                              entry.timeEntryId,
                                              provider.providerName,
                                              facility.facilityName,
                                              formatDateInTimezone(
                                                entry.submittedStartsAt,
                                                entry.timezoneId,
                                              ),
                                            )
                                          }
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <XCircle className="h-4 w-4 mr-1" />
                                          Reject
                                        </Button>
                                      </div>
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Mobile cards */}
                      <div className="sm:hidden space-y-2 p-3">
                        {facility.entries.map((entry) => {
                          const isSelected = selectedIds.has(entry.timeEntryId);
                          const scheduledHours = calculateHours(
                            new Date(entry.scheduledStartsAt),
                            new Date(entry.scheduledEndsAt),
                          );
                          const submittedHours = calculateHours(
                            new Date(entry.submittedStartsAt),
                            new Date(entry.submittedEndsAt),
                          );
                          const difference = getEntryHoursDifference(entry);

                          return (
                            <Card
                              key={entry.timeEntryId}
                              className={cn(
                                "p-3",
                                canApprove &&
                                  isSelected &&
                                  "bg-blue-50 border-blue-200",
                              )}
                            >
                              <CardContent className="p-0 space-y-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-3">
                                    {canApprove && (
                                      <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() =>
                                          toggleEntry(entry.timeEntryId)
                                        }
                                        className="mt-1"
                                      />
                                    )}
                                    <div>
                                      <div className="font-medium">
                                        {formatDateInTimezone(
                                          entry.submittedStartsAt,
                                          entry.timezoneId,
                                        )}
                                      </div>
                                      {isAdHocEntry(entry) && (
                                        <AdHocEntryBadge className="mt-1" />
                                      )}
                                      <div className="text-sm text-slate-500">
                                        {formatTimeInTimezone(
                                          entry.submittedStartsAt,
                                          entry.timezoneId,
                                        )}{" "}
                                        -{" "}
                                        {formatTimeInTimezone(
                                          entry.submittedEndsAt,
                                          entry.timezoneId,
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <EntryDifferenceBadge
                                    difference={difference}
                                    className="text-xs"
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <div className="text-xs text-slate-500 mb-1">
                                      Scheduled
                                    </div>
                                    {isAdHocEntry(entry) && (
                                      <div className="text-slate-500">
                                        {NOT_SCHEDULED_LABEL}
                                      </div>
                                    )}
                                    {!isAdHocEntry(entry) && (
                                      <>
                                        <div className="text-slate-600">
                                          {formatTimeInTimezone(
                                            entry.scheduledStartsAt,
                                            entry.timezoneId,
                                          )}{" "}
                                          -{" "}
                                          {formatTimeInTimezone(
                                            entry.scheduledEndsAt,
                                            entry.timezoneId,
                                          )}
                                        </div>
                                        <div className="text-slate-400">
                                          {formatHours(scheduledHours)}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-xs text-slate-500 mb-1">
                                      Submitted
                                    </div>
                                    <div className="font-medium text-slate-900">
                                      {formatTimeInTimezone(
                                        entry.submittedStartsAt,
                                        entry.timezoneId,
                                      )}{" "}
                                      -{" "}
                                      {formatTimeInTimezone(
                                        entry.submittedEndsAt,
                                        entry.timezoneId,
                                      )}
                                    </div>
                                    <div className="text-blue-600">
                                      {formatHours(submittedHours)}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between pt-2 border-t">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm">
                                      {formatWageType(entry.baseWageType)}
                                    </span>
                                    {entry.isOvertime && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                                      >
                                        OT
                                      </Badge>
                                    )}
                                  </div>
                                  {canApprove && (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          onApproveOne(entry.timeEntryId)
                                        }
                                        disabled={isApproving}
                                        className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 px-2"
                                      >
                                        <CheckCircle className="h-4 w-4 mr-1" />
                                        Approve
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          onReject(
                                            entry.timeEntryId,
                                            provider.providerName,
                                            facility.facilityName,
                                            formatDateInTimezone(
                                              entry.submittedStartsAt,
                                              entry.timezoneId,
                                            ),
                                          )
                                        }
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                                      >
                                        <XCircle className="h-4 w-4 mr-1" />
                                        Reject
                                      </Button>
                                    </div>
                                  )}
                                </div>

                                <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-2">
                                  <WageSummary entry={entry} />
                                  {canApprove && (
                                    <>
                                      <EditCustomWageButton
                                        entry={entry}
                                        organizationBusinessUnitId={
                                          organizationBusinessUnitId
                                        }
                                        onSaved={onOrgNoteSaved}
                                      />
                                      {entry.organizationWageSet && (
                                        <SwitchOrganizationWageSetButton
                                          entry={entry}
                                          organizationWageSet={
                                            entry.organizationWageSet
                                          }
                                          organizationBusinessUnitId={
                                            organizationBusinessUnitId
                                          }
                                          onSaved={onOrgNoteSaved}
                                        />
                                      )}
                                    </>
                                  )}
                                </div>

                                {entry.providerNote && (
                                  <div className="pt-2 border-t text-sm">
                                    <div className="text-xs text-slate-500 mb-1">
                                      Provider Note
                                    </div>
                                    <div className="text-slate-600">
                                      {entry.providerNote}
                                    </div>
                                  </div>
                                )}
                                {(canApprove || entry.orgNote) && (
                                  <div className="pt-2 border-t text-sm">
                                    <div className="mb-1 flex items-center gap-1 text-xs font-medium text-slate-500">
                                      <FileLock2 className="h-3.5 w-3.5" />
                                      Org Note
                                    </div>
                                    <TimeEntryOrgNote
                                      timeEntryId={entry.timeEntryId}
                                      organizationBusinessUnitId={
                                        organizationBusinessUnitId
                                      }
                                      orgNote={entry.orgNote}
                                      canEdit={canApprove}
                                      popover
                                      onSaved={onOrgNoteSaved}
                                    />
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>

                      {/* Provider notes section (desktop) */}
                      {facility.entries.some((e) => e.providerNote) && (
                        <div className="hidden sm:block px-4 py-3 bg-slate-50 border-t">
                          <div className="text-xs font-semibold text-slate-500 mb-2">
                            Provider Notes:
                          </div>
                          {facility.entries
                            .filter((e) => e.providerNote)
                            .map((entry) => (
                              <div
                                key={entry.timeEntryId}
                                className="text-sm text-slate-600 mb-1"
                              >
                                <span className="font-medium">
                                  {formatDateInTimezone(
                                    entry.submittedStartsAt,
                                    entry.timezoneId,
                                  )}
                                  :
                                </span>{" "}
                                {entry.providerNote}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}
    </div>
  );
}
